import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiPlus,
  FiChevronRight,
  FiBluetooth,
  FiCheck,
  FiUser,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { RICChannelWebBLE } from "@robotical/ricjs";
import {
  addRobotsToUser,
  getSerialNumberFromDevice,
} from "../../utils/add-robots";
import {
  getUserGivenEmail,
  isSerialNumberRegistered,
} from "../../utils/onboarding";
import { RegisteredUser } from "../../types/generic";
import { useTickets } from "../../store/ticket-context";
import {
  Badge,
  Button,
  PageHeading,
  MartyArtwork,
  LoadingState,
  EmptyState,
  ErrorState,
  formatDate,
} from "../UI";
import Modal from "../Modal";
import TicketItem from "../Tickets/Item";
import styles from "./styles.module.css";
type Props = { user: RegisteredUser; setUser: (user: RegisteredUser) => void };
export default function UserRobots({ user, setUser }: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const { tickets, loading, failedRobots, pending, updateStatus, reload } =
    useTickets();
  const location = useLocation();
  const [devices, setDevices] = useState<BluetoothDevice[]>([]);
  const [isLoadingMessage, setIsLoadingMessage] = useState<string>("");
  const [isBLESupported, setIsBLESupported] = useState<boolean>(false);

  useEffect(() => {
    if (!navigator.bluetooth) {
      // toast.error("Bluetooth is not supported in this browser.", { autoClose: 5000 });
      setIsBLESupported(false);
    } else {
      setIsBLESupported(true);
    }
  }, []);

  const getOtherSerialNumbers = async () => {
    const otherSerNums = [];
    for (const device of devices) {
      try {
        const serialNumber = await getSerialNumberFromDevice(device);
        if (
          serialNumber &&
          (!user.serialNumbers || !user.serialNumbers[serialNumber])
        ) {
          const isSerialNoRegistered =
            await isSerialNumberRegistered(serialNumber);
          if (!isSerialNoRegistered) {
            otherSerNums.push(serialNumber);
          } else {
            toast.warn(
              `${serialNumber} is already registered, it will not be added.`,
              { autoClose: 5000 },
            );
          }
        } else {
          toast.warn(
            `${serialNumber} is already in your robots, it will not be added.`,
            { autoClose: 5000 },
          );
        }
      } catch (error) {
        toast.error(`Failed to get serial number from ${device.name}.`, {
          autoClose: 5000,
        });
        console.error("Error:", error);
      }
    }

    return otherSerNums;
  };

  const saveSerialNumbers = async (otherSerNums: string[]) => {
    const didAdd = await addRobotsToUser(user.email, otherSerNums);
    if (didAdd) {
      const fetchedUser = await getUserGivenEmail(user.email, true);
      if (fetchedUser) {
        setUser(fetchedUser);
      }
      toast.success("Robots added successfully.", { autoClose: 5000 });
      return true;
    } else {
      toast.error("Failed to add robots.", { autoClose: 5000 });
      return false;
    }
  };

  const handleSave = async () => {
    if (devices.length === 0 || isLoadingMessage || isScanning) return;
    setIsLoadingMessage("Scanning for more robots...");

    try {
      setIsLoadingMessage("Getting serial numbers...");
      const otherSerNums = await getOtherSerialNumbers();
      if (!otherSerNums.length) {
        setIsLoadingMessage("");
        return;
      }

      setIsLoadingMessage("Saving serial numbers...");
      const didSave = await saveSerialNumbers(otherSerNums);
      if (!didSave) {
        setIsLoadingMessage("");
        return;
      }

      setDevices([]);
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoadingMessage("");
    }
  };

  const handleAdd = async () => {
    if (isScanning || isLoadingMessage || !navigator.bluetooth) return;
    setIsScanning(true);
    const namesToIgnore = devices.map((device_) => ({ name: device_.name }));
    try {
      const device = await navigator.bluetooth.requestDevice({
        // acceptAllDevices: true,
        filters: [{ services: [RICChannelWebBLE.RICServiceUUID] }],
        // @ts-ignore
        exclusionFilters: namesToIgnore.length > 0 ? namesToIgnore : undefined,
        optionalServices: [],
      });
      // add the device to the list of devices only if it's not already there
      if (!devices.find((deviceInList) => deviceInList.id === device.id)) {
        setDevices((prevDevices) => [...prevDevices, device]);
        toast.success(`${device.name} added to the list of robots.`, {
          autoClose: 5000,
        });
      } else {
        toast.error(`${device.name} is already in the list of robots.`, {
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsScanning(false);
    }
  };

  const robots = Object.values(user.serialNumbers || {}).sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
  const openTickets = tickets.filter(
    (entry) => entry.ticket.status !== "Closed",
  );
  const closeDialog = () => {
    if (isScanning || isLoadingMessage) return;
    setDevices([]);
    setIsDialogOpen(false);
  };
  const ticketLink = (serialNumber?: string) => {
    const params = new URLSearchParams(location.search);
    if (serialNumber) params.set("robot", serialNumber);
    else params.delete("robot");
    return { pathname: "/my-stats", search: params.toString() };
  };
  return (
    <>
      <PageHeading
        title="My robots"
        count={
          <span className={styles.registeredCount}>
            {robots.length} registered
          </span>
        }
        action={
          <Button variant="primary" onClick={() => setIsDialogOpen(true)}>
            <FiPlus aria-hidden="true" />
            Add robot
          </Button>
        }
      />
      <p className={styles.mobileCount}>{robots.length} registered</p>
      {robots.length ? (
        <div className={styles.grid}>
          {robots.map((robot) => {
            const openCount = openTickets.filter(
              (entry) => entry.serialNumber === robot.serialNumber,
            ).length;
            const failed = failedRobots.includes(robot.serialNumber);
            return (
              <article
                className={styles.card}
                key={robot.serialNumber}
                aria-label={robot.name || robot.serialNumber}
              >
                <div className={styles.identity}>
                  <div>
                    <h2>{robot.name || "Marty"}</h2>
                    <p>{robot.serialNumber}</p>
                  </div>
                  <div className={styles.artwork}>
                    <MartyArtwork />
                  </div>
                </div>
                <div className={styles.registration}>
                  <span>Registered</span>
                  <p>{formatDate(robot.date)}</p>
                </div>
                <div className={styles.footer}>
                  <Badge
                    status={loading || failed || !openCount ? "none" : "open"}
                  >
                    {loading
                      ? "Loading tickets…"
                      : failed
                        ? "Tickets unavailable"
                        : openCount
                          ? `${openCount} open ticket${openCount === 1 ? "" : "s"}`
                          : "No open tickets"}
                  </Badge>
                  <Link
                    className={styles.ticketLink}
                    to={ticketLink(robot.serialNumber)}
                  >
                    <FiChevronRight size={18} aria-hidden="true" />
                    View tickets
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title="No robots registered yet"
          description="Add a robot using Bluetooth."
          artwork={<MartyArtwork />}
        >
          <Button variant="primary" onClick={() => setIsDialogOpen(true)}>
            <FiPlus aria-hidden="true" />
            Add robot
          </Button>
        </EmptyState>
      )}
      {robots.length > 0 && (
        <section
          className={styles.activity}
          aria-labelledby="open-tickets-heading"
        >
          <div className={styles.activityHeading}>
            <h2 id="open-tickets-heading">Open service tickets</h2>
            {!loading && !failedRobots.length && (
              <Badge status={openTickets.length ? "open" : "none"}>
                {openTickets.length}
              </Badge>
            )}
            <Link className={styles.ticketLink} to={ticketLink()}>
              <FiChevronRight size={18} aria-hidden="true" />
              View all tickets
            </Link>
          </div>
          {loading ? (
            <LoadingState label="Loading tickets…" />
          ) : failedRobots.length ? (
            <ErrorState
              message="Some tickets could not be loaded."
              onRetry={reload}
            />
          ) : openTickets.length ? (
            openTickets
              .slice(0, 3)
              .map((entry) => (
                <TicketItem
                  key={`${entry.serialNumber}:${entry.id}`}
                  ticket={entry.ticket}
                  robotName={entry.robotName}
                  serialNumber={entry.serialNumber}
                  pending={pending.includes(
                    `${entry.serialNumber}:${entry.id}`,
                  )}
                  onUpdateStatus={(status) => updateStatus(entry, status)}
                />
              ))
          ) : (
            <p className={styles.noTickets}>No open tickets</p>
          )}
        </section>
      )}
      {user.establishment && (
        <p className={styles.accountContext}>
          <FiUser size={16} aria-hidden="true" />
          Registered to {user.establishment}
        </p>
      )}
      <Modal
        show={isDialogOpen}
        title="Add robot"
        size="small"
        onClose={closeDialog}
        closeDisabled={!!isLoadingMessage || isScanning}
      >
        {devices.length ? (
          <>
            <div className={styles.selectedRobots}>
              {devices.map((device) => (
                <div className={styles.selectedRobot} key={device.id}>
                  <MartyArtwork small />
                  <div>
                    <strong>{device.name || "Marty"}</strong>
                    <p>Device ID · {device.id}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className={styles.ready}>
              <FiCheck size={18} aria-hidden="true" />
              {devices.length} robot{devices.length === 1 ? "" : "s"} ready to
              register
            </p>
            <Button
              onClick={handleAdd}
              disabled={isScanning || !!isLoadingMessage}
            >
              <FiPlus aria-hidden="true" />
              {isScanning ? "Finding robots…" : "Add another robot"}
            </Button>
          </>
        ) : (
          <>
            <div className={styles.bluetooth}>
              <FiBluetooth size={32} aria-hidden="true" />
            </div>
            <strong>
              {isBLESupported
                ? "Keep your robot nearby and switched on."
                : "Bluetooth is unavailable"}
            </strong>
            <p className={styles.hint}>
              {isBLESupported
                ? "Choose your robot in the browser’s Bluetooth picker."
                : "Open this dashboard in a browser that supports Bluetooth to add a robot."}
            </p>
            {isBLESupported && (
              <Button
                variant="primary"
                onClick={handleAdd}
                disabled={isScanning}
              >
                <FiBluetooth aria-hidden="true" />
                {isScanning ? "Finding robots…" : "Find nearby robots"}
              </Button>
            )}
          </>
        )}
        {isLoadingMessage && <LoadingState label={isLoadingMessage} />}
        <div className={styles.dialogFooter}>
          <Button
            onClick={closeDialog}
            disabled={isScanning || !!isLoadingMessage}
          >
            Cancel
          </Button>
          {devices.length > 0 && (
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!!isLoadingMessage || isScanning}
            >
              Register robot{devices.length === 1 ? "" : "s"}
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
}
